import { Suspense, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { graphql, useLazyLoadQuery, usePaginationFragment, useMutation } from 'react-relay';
import { QueryErrorBoundary } from '@/components/QueryErrorBoundary';
import type { FollowingPageQuery } from '@/__generated__/FollowingPageQuery.graphql';
import type { FollowingPage_topics$key } from '@/__generated__/FollowingPage_topics.graphql';
import type { FollowingPageUnfollowMutation } from '@/__generated__/FollowingPageUnfollowMutation.graphql';

/**
 * Root query for FollowingPage - fetches topics followed by the viewer
 */
const followingPageQuery = graphql`
  query FollowingPageQuery {
    ...FollowingPage_topics
  }
`;

/**
 * Fragment for paginated followed topics
 */
const FollowingPage_topics = graphql`
  fragment FollowingPage_topics on QueryRoot
  @refetchable(queryName: "FollowingPageRefetchQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 20 }
    after: { type: "String" }
  ) {
    followedTopics(first: $first, after: $after)
      @connection(key: "FollowingPage_followedTopics") {
      edges {
        node {
          id
          slug
          name
          s3AnchorImagePath
          isFollowedByViewer
          followerCount
          episodes(first: 1) {
            totalCount
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Mutation to unfollow a topic
 */
const unfollowTopicMutation = graphql`
  mutation FollowingPageUnfollowMutation($input: UnfollowTopicInput!) {
    unfollowTopic(input: $input) {
      topic {
        id
        isFollowedByViewer
        followerCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

interface TopicData {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly s3AnchorImagePath: string | null | undefined;
  readonly isFollowedByViewer: boolean | null | undefined;
  readonly followerCount: number;
  readonly episodes: {
    readonly totalCount: number | null | undefined;
  };
}

interface TopicCardProps {
  topic: TopicData;
  onUnfollow: (topicSlug: string) => void;
  isUnfollowing: boolean;
}

function TopicCard({ topic, onUnfollow, isUnfollowing }: TopicCardProps) {
  const cdnHost = import.meta.env.VITE_CDN_HOST;
  const imageUrl = topic.s3AnchorImagePath
    ? `${cdnHost}/${topic.s3AnchorImagePath}`
    : null;

  return (
    <div className="group relative rounded-xl border border-gray-800 bg-gradient-to-b from-gray-800/50 to-gray-900/50 overflow-hidden transition-all hover:border-gray-700">
      {/* Cover image or placeholder */}
      <div className="aspect-square w-full bg-gray-800">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={topic.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-900/50 to-purple-900/50">
            <svg
              className="h-12 w-12 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Topic info */}
      <div className="p-4">
        <Link
          to={`/p/${topic.slug}`}
          className="block font-semibold text-white hover:text-indigo-400 transition-colors line-clamp-1"
        >
          {topic.name}
        </Link>

        <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
          <span>{topic.episodes.totalCount ?? 0} episode{topic.episodes.totalCount !== 1 ? 's' : ''}</span>
          <span>•</span>
          <span>{topic.followerCount} follower{topic.followerCount !== 1 ? 's' : ''}</span>
        </div>

        {/* Unfollow button */}
        <button
          type="button"
          onClick={() => onUnfollow(topic.slug)}
          disabled={isUnfollowing}
          className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUnfollowing ? (
            <>
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Unfollowing...
            </>
          ) : (
            <>
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M22 10.5h-6m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM4 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 10.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
                />
              </svg>
              Unfollow
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-800">
        <svg
          className="h-8 w-8 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">No followed topics</h3>
      <p className="text-gray-400 mb-6 max-w-md mx-auto">
        You haven't followed any topics yet. Discover topics you're interested in and follow them to get updates on new episodes.
      </p>
      <Link
        to="/dashboard/discover"
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>
        Discover Topics
      </Link>
    </div>
  );
}

interface FollowingPageContentProps {
  queryRef: FollowingPage_topics$key;
}

function FollowingPageContent({ queryRef }: FollowingPageContentProps) {
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    FollowingPage_topics,
    queryRef
  );

  const [commitUnfollow, isUnfollowingMutation] = useMutation<FollowingPageUnfollowMutation>(unfollowTopicMutation);

  const topics = useMemo(() => {
    return data.followedTopics.edges.map((edge) => edge.node);
  }, [data.followedTopics.edges]);

  const handleUnfollow = useCallback((topicSlug: string) => {
    const topic = topics.find((t) => t.slug === topicSlug);
    commitUnfollow({
      variables: {
        input: { topicSlug },
      },
      optimisticUpdater: (store) => {
        // Update the topic's isFollowedByViewer and followerCount
        if (topic) {
          const topicRecord = store.get(topic.id);
          if (topicRecord) {
            const currentFollowerCount = topicRecord.getValue('followerCount') as number | null;
            topicRecord.setValue(false, 'isFollowedByViewer');
            topicRecord.setValue(Math.max(0, (currentFollowerCount ?? 0) - 1), 'followerCount');
          }
        }
        // Remove the unfollowed topic from the connection
        const queryRecord = store.getRoot();
        const connection = queryRecord.getLinkedRecord('followedTopics', { first: 20 });
        if (connection) {
          const edges = connection.getLinkedRecords('edges');
          if (edges) {
            const newEdges = edges.filter((edge) => {
              const node = edge.getLinkedRecord('node');
              if (node) {
                return node.getValue('slug') !== topicSlug;
              }
              return true;
            });
            connection.setLinkedRecords(newEdges, 'edges');
          }
        }
      },
      onError: (error) => {
        console.error('Failed to unfollow topic:', error);
      },
    });
  }, [commitUnfollow, topics]);

  if (topics.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Following</h2>
        <p className="mt-1 text-gray-400">
          Topics you're following ({data.followedTopics.totalCount})
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {topics.map((topic) => (
          <TopicCard
            key={topic.id}
            topic={topic}
            onUnfollow={handleUnfollow}
            isUnfollowing={isUnfollowingMutation}
          />
        ))}
      </div>

      {hasNext && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => loadNext(20)}
            disabled={isLoadingNext}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingNext ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}
    </>
  );
}

function FollowingPageLoader() {
  const data = useLazyLoadQuery<FollowingPageQuery>(followingPageQuery, {}, {
    fetchPolicy: 'store-and-network',
  });

  return <FollowingPageContent queryRef={data} />;
}

export function FollowingPage() {
  return (
    <QueryErrorBoundary>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        }
      >
        <FollowingPageLoader />
      </Suspense>
    </QueryErrorBoundary>
  );
}
